import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../types/rootState'
import { defineMessages, useIntl } from 'react-intl'
import { RefiDetail } from '../../../types/refinance'
import { RefiOptionHeader } from './RefiOptionHeader'
import { ARM_MORTGAGE_TYPE } from '../../../constants/loan'
import RefiMessages from '../RefiModule/RefiByYearsInHome/RefiMessages'
import { Card, Flex, CardBody, Button } from '@homebotapp/hb-react-component-catalog'
import { sampleRefiDetailsLongtermSavings } from '../../../constants/homePreview'
import { HOMEOWNER_DIGEST_PRINCIPLE_AND_INTEREST_REFI_OPTION_CARD } from '../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'

const MSG = defineMessages({
  yourRateButton: {
    id: 'RefiOption.button.whatRate.narrow',
    defaultMessage: 'Get rate'
  }
})

export interface RefiOptionProps {
  locked: boolean
  detail: RefiDetail
  yearsInHome: number
  onClick: (e) => void
}

export const RefiOption = ({ locked, detail, onClick, yearsInHome }: RefiOptionProps) => {
  const intl = useIntl()
  const [hover, setHover] = useState(false)
  const pmiPayment = useSelector<RootState, number | null>(state => state.pmi.monthlyPmiPayment)

  const handleClick = e => {
    e.stopPropagation()
    onClick(e)
  }

  if (!detail.longterm && !locked) return null

  return (
    <Card
      flex='1 1 0'
      margin='0 auto'
      maxWidth='20rem'
      cursor='pointer'
      onClick={!locked ? handleClick : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      transition={'transform .1s ease-in-out'}
      transform={hover ? 'scale3d(1.03, 1.03, 1)' : undefined}
      data-qa={`RefiOption-Details-${detail.years}-${detail.type.toLowerCase()}-horizontal`}
    >
      <CardBody display='flex' flexDirection='column' padding={2}>
        <Flex h='100%' gap={4} direction={['row', null, 'column']}>
          <RefiOptionHeader
            type={detail.type}
            years={detail.years}
            rate={detail.rate}
            apr={detail.apr}
            longterm={locked ? sampleRefiDetailsLongtermSavings : detail.longterm}
            caution={detail.type === ARM_MORTGAGE_TYPE && yearsInHome > detail.years}
            locked={locked}
          />
          <Flex marginY={3} direction='column' justify='flex-start' flex='0 1 auto'>
            <RefiMessages.ShortBullets
              yearsInHome={yearsInHome}
              locked={locked}
              pmi={pmiPayment}
              type={detail.type}
              years={detail.years}
              rate={detail.rate}
              shortterm={detail.shortterm}
              longterm={locked ? sampleRefiDetailsLongtermSavings : detail.longterm}
            />
          </Flex>
        </Flex>
        <Button
          size='sm'
          data-qa='refi-more-details-button'
          onClick={!locked ? handleClick : undefined}
          tracking={{
            guid: 'rLmWt9Deip5g5P2wAYJf72',
            ui_context: HOMEOWNER_DIGEST_PRINCIPLE_AND_INTEREST_REFI_OPTION_CARD,
            descriptor:
              detail.type === 'ARM'
                ? `${detail.years}/1 ARM: ${MSG.yourRateButton.defaultMessage}`
                : `${detail.years} Year Fixed: ${MSG.yourRateButton.defaultMessage}`
          }}
        >
          {intl.formatMessage(MSG.yourRateButton)}
        </Button>
      </CardBody>
    </Card>
  )
}
